import * as THREE from 'three';
import { BLACK, BONE, DSTEEL, DWOOD, WOOD, cloth, emis, leather, metal, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { mulberry32 } from '../rand.ts';
import { gear } from './clockwork.ts';
import { hornedSkull, roundShield } from './class-buildings.ts';
import { IRON_BAND, IRON_PLATE, IRON_RIVET, IRON_STONE, type Wear } from './structures.ts';
import type { Model } from './types.ts';

/*
 * What a level adds to a building or a trap (docs/OBRAS.md §4: on terraced land the level changes what it does,
 * so it has to show). Level 1 is the model as it always was; each level above it adds a module the table camera
 * reads: a second still, a turret bay, a trophy pole, braziers, standards; a trap grows nastier up to level 5.
 * Geometry only, over the paints the models already use (the factories are cached by their arguments, so the
 * same call is the same material): no new materials, nothing translucent or two-sided, no lights; glows are
 * glow sprites, baked with the model. Every module is drawn after the model with its own seeded rng, so level 1
 * stays exactly as it was.
 */

/** Types whose level shows (content LAND_LEVELS buildings to 3, traps to 5 on terraced land, LAND_ERAS.trapTop). */
export const LEVELED_TYPES = ['temple', 'grove', 'lab', 'workshop', 'hall', 'watchtower', 'sanctum', 'depot', 'spikes', 'oil'];

// Paints already in use by the models (same factory, same arguments: the same cached material).
const COPPER = metal('#a4512f', { roughness: 0.4 });
const PLATE = std('#808a96', { metalness: 0.5, roughness: 0.38, flatShading: true });
const CRIMSON = cloth('#7a1d22');
const EMBLEM = cloth('#d9dce3');
const SILVER = metal('#c9d0da', { roughness: 0.3 });
const VIGIL_LIT = emis('#f2f6ff', '#bcd0ff', 1.4);
const FONT = emis('#dff4ff', '#7fd6ff', 1.1);
const RUNE = emis('#ffc0a8', '#ff4f3a', 1.3);
const FORGE = emis('#ffb070', '#ff5a10', 1.4);
const EMBER = emis('#ff9a50', '#ff6a1a', 0.5);
const PLINTH = stone('#5f5b55');
const BARK = std('#3e3128', { roughness: 1, bumpScale: 0.04 });
const MOSS = std('#5d7a3a', { roughness: 1, bumpScale: 0.04 });
const LOG_END = std('#a07850', { roughness: 0.9 });
const SACK = cloth('#6a5f50');
const HIDE = leather('#7d6a58');
const RED_BAND = cloth('#8e1f22');
const OIL_IRON = metal('#2e2c30', { roughness: 0.5 });
const brew = (c: string): THREE.MeshStandardMaterial => emis(c, c, 1.1, { roughness: 0.2, bumpScale: 0 });
const GLYPH = emis('#d8f5c0', '#6fa05a', 0.9, { flatShading: true });

type Mat = 1 | 2 | 3;
const wallOf = (mat: Mat): THREE.MeshStandardMaterial => (mat === 1 ? stone('#77736c') : stone(mat === 3 ? IRON_STONE : '#8b857a'));
const paleOf = (mat: Mat): THREE.MeshStandardMaterial => (mat === 1 ? stone('#8f8a80') : mat === 3 ? stone('#9a9ea6') : stone('#c3beb4'));
const postOf = (mat: Mat): THREE.Material => (mat === 3 ? IRON_BAND : DWOOD);
const roofOf = (mat: Mat): THREE.Material =>
  mat === 1 ? std('#4a2f22', { roughness: 0.95, flatShading: true }) : mat === 3 ? metal('#3d434b', { roughness: 0.42, metalness: 0.6, flatShading: true }) : stone('#3e3a36');

const box = (parent: THREE.Object3D, mat: THREE.Material, w: number, h: number, d: number, x: number, y: number, z: number, ry = 0): THREE.Mesh => {
  const m = M(new THREE.BoxGeometry(w, h, d), mat, parent);
  m.position.set(x, y, z);
  m.rotation.y = ry;
  return m;
};

/** Glow strength by wear. */
const dimOf = (wear: Wear): number => [1, 0.65, 0.3][wear] as number;

/** A standard of the Order: a pole, a long crimson hanging with its pale emblem, a silver knob (faces +Z). */
const standard = (parent: THREE.Object3D, x: number, z: number, h: number, ry: number, wear: Wear, y0 = 0): void => {
  const pg = G(parent, x, y0, z);
  pg.rotation.y = ry;
  // Crumbling: the pole leans.
  if (wear === 2) pg.rotation.z = 0.22;
  limb(pg, V(0, 0, 0), V(0, h, 0), 0.016, 0.018, DWOOD, 5);
  box(pg, DWOOD, 0.2, 0.02, 0.02, 0, h - 0.04, 0.01);
  const long = wear === 2 ? 0.2 : 0.32;
  box(pg, CRIMSON, 0.17, long, 0.012, 0, h - 0.05 - long / 2, 0.02);
  box(pg, EMBLEM, 0.06, 0.06, 0.016, 0, h - 0.15, 0.022).rotation.z = Math.PI / 4;
  M(new THREE.SphereGeometry(0.024, 6, 4), SILVER, pg).position.y = h + 0.02;
};

/** A post with a lantern hung from an arm, lit (faces +Z). */
const lanternPost = (parent: THREE.Object3D, x: number, z: number, h: number, ry: number, wear: Wear, y0 = 0, color = '#ff8a3a'): void => {
  const pg = G(parent, x, y0, z);
  pg.rotation.y = ry;
  box(pg, PLINTH, 0.09, 0.04, 0.09, 0, 0.02, 0);
  box(pg, DWOOD, 0.045, h, 0.045, 0, h / 2, 0);
  box(pg, DWOOD, 0.15, 0.03, 0.03, 0.06, h - 0.03, 0);
  const lan = G(pg, 0.12, h - 0.11, 0);
  limb(lan, V(0, 0.07, 0), V(0, 0.03, 0), 0.004, 0.004, DSTEEL, 3);
  M(new THREE.CylinderGeometry(0.03, 0.035, 0.06, 6, 1, true), DSTEEL, lan);
  box(lan, EMBER, 0.028, 0.04, 0.028, 0, 0, 0);
  M(new THREE.ConeGeometry(0.042, 0.035, 6), DSTEEL, lan).position.y = 0.045;
  glowSprite(lan, color, 0.3, V(0, 0, 0.01), 0.75 * dimOf(wear));
};

/** A stand of logs, ends to the field (faces +Z). */
const logStack = (parent: THREE.Object3D, x: number, y: number, z: number, ry: number, rows: number[], r = 0.04, len = 0.3): void => {
  const sg = G(parent, x, y, z);
  sg.rotation.y = ry;
  rows.forEach((n, j) => {
    for (let i = 0; i < n; i++) {
      const lg = G(sg, (i - (n - 1) / 2) * r * 2.05, r + j * r * 1.75, 0);
      const cy = M(new THREE.CylinderGeometry(r, r * 1.05, len, 7), WOOD, lg);
      cy.rotation.x = Math.PI / 2;
      M(new THREE.CircleGeometry(r * 0.92, 7), LOG_END, lg).position.z = len / 2 + 0.002;
    }
  });
  for (const k of [-1, 1]) box(sg, DWOOD, 0.03, rows.length * r * 1.9, 0.03, k * (rows[0]! * r + 0.02), rows.length * r * 0.95, len / 2 - 0.04);
};

/** A wooden crate with dark edges. */
const crate = (parent: THREE.Object3D, s: number, x: number, y: number, z: number, ry: number): void => {
  const cg = G(parent, x, y, z);
  cg.rotation.y = ry;
  box(cg, WOOD, s, s * 0.8, s, 0, s * 0.4, 0);
  for (const k of [-1, 1]) box(cg, DWOOD, s + 0.01, 0.014, 0.014, 0, s * 0.4 + k * s * 0.3, s / 2 + 0.002);
  box(cg, DWOOD, 0.014, s * 0.82, 0.014, 0, s * 0.4, s / 2 + 0.004).rotation.z = 0.7;
};

/* ---------- buildings (levels 2 and 3) ---------- */

/**
 * Temple. 2 (more reach): four waystones at the corners of the step, their pale runes lit like the font.
 * 3 (more for the towers): a stone lantern crowns the roof, the font's light burning in it.
 */
const temple = (g: THREE.Object3D, lv: number, mat: Mat, wear: Wear): void => {
  const st = stone('#a9a397');
  const dim = dimOf(wear);
  // On the middle of each side of the step: the front and right ones face the table camera.
  for (const [x, z] of [[0, 1], [1, 0], [0, -1], [-1, 0]] as const) {
    const sx = x * 0.5, sz = z * 0.5;
    const a = Math.atan2(sx, sz);
    const fallen = wear === 2 && x > 0;
    const s = G(g, sx, 0.08, sz);
    s.rotation.y = a;
    if (fallen) {
      s.rotation.set(Math.PI / 2 - 0.1, a, 0);
      s.position.set(sx + 0.08, 0.06, sz + 0.1);
    }
    box(s, st, 0.12, 0.46, 0.09, 0, 0.23, 0);
    M(new THREE.ConeGeometry(0.085, 0.09, 4), st, s).position.y = 0.505;
    // The rune: a stroke and a bar, toward the field.
    box(s, FONT, 0.024, 0.2, 0.012, 0, 0.27, 0.047);
    box(s, FONT, 0.08, 0.02, 0.012, 0, 0.33, 0.047);
    if (!fallen) glowSprite(s, '#9fe0ff', 0.5, V(0, 0.3, 0.08), 0.85 * dim);
  }
  if (lv < 3) return;
  if (wear === 2) {
    // The roof sagged: the lantern lies toppled on the step.
    const l = G(g, -0.3, 0.12, 0.42);
    l.rotation.set(0, 0.4, Math.PI / 2);
    box(l, st, 0.14, 0.18, 0.14, 0, 0, 0);
    box(l, FONT, 0.06, 0.08, 0.145, 0, 0, 0);
    return;
  }
  const y = 1.33;
  box(g, st, 0.16, 0.04, 0.16, 0, y, 0);
  for (const [x, z] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) box(g, st, 0.035, 0.16, 0.035, x * 0.06, y + 0.1, z * 0.06);
  M(new THREE.OctahedronGeometry(0.05, 0), FONT, g).position.set(0, y + 0.1, 0);
  box(g, st, 0.18, 0.04, 0.18, 0, y + 0.2, 0);
  const cap = M(new THREE.ConeGeometry(0.12, 0.16, 4), stone('#3e3a36'), g);
  cap.position.y = y + 0.3;
  cap.rotation.y = Math.PI / 4;
  glowSprite(g, '#9fe0ff', 0.7, V(0, y + 0.1, 0), 0.7 * dim);
};

/**
 * Grove. 2 (more wood): a cord of cut logs at the side and a young oak. 3 (roots slow more): great roots arch out
 * of the mound toward the field, mossed, a green glyph glowing on each.
 */
const grove = (g: THREE.Object3D, lv: number, mat: Mat, wear: Wear): void => {
  const Y0 = 0.1;
  // The cord of logs at the front right, its cut ends to the field; a chopping block with its axe by it.
  logStack(g, 0.38, Y0, 0.36, 0.25, wear === 2 ? [4, 2] : [4, 3, 2], 0.04, 0.26);
  const blk = M(new THREE.CylinderGeometry(0.06, 0.065, 0.09, 8), WOOD, g);
  blk.position.set(0.12, Y0 + 0.045, 0.56);
  const cut = M(new THREE.CircleGeometry(0.055, 8), LOG_END, g);
  cut.position.set(0.12, Y0 + 0.092, 0.56);
  cut.rotation.x = -Math.PI / 2;
  limb(g, V(0.1, Y0 + 0.09, 0.56), V(0.04, Y0 + 0.22, 0.62), 0.008, 0.008, DWOOD, 4);
  box(g, DSTEEL, 0.05, 0.035, 0.01, 0.1, Y0 + 0.1, 0.56, 0.6);
  if (lv < 3) return;
  const dim = dimOf(wear);
  // Toward the camera's sides of the mound: right, front left and back right.
  for (const [a, span, h] of [[1.3, 0.3, 0.2], [-0.75, 0.28, 0.18], [2.3, 0.28, 0.2]] as const) {
    const d0 = 0.4, d1 = d0 + span;
    const pa = V(Math.sin(a - 0.12) * d0, Y0 - 0.02, Math.cos(a - 0.12) * d0);
    const pb = V(Math.sin(a + 0.12) * d1, -0.01, Math.cos(a + 0.12) * d1);
    const mid = pa.clone().lerp(pb, 0.5);
    mid.y = h;
    const curve = new THREE.CatmullRomCurve3([pa, pa.clone().lerp(mid, 0.5).setY(h * 0.85), mid, mid.clone().lerp(pb, 0.5).setY(h * 0.8), pb]);
    M(new THREE.TubeGeometry(curve, 10, 0.045, 5), BARK, g);
    const moss = M(new THREE.DodecahedronGeometry(0.035, 0), MOSS, g);
    moss.position.copy(mid).add(V(0, 0.03, 0));
    moss.scale.y = 0.5;
    const out = V(Math.sin(a), 0, Math.cos(a));
    const gl = M(new THREE.TorusGeometry(0.03, 0.008, 3, 10), GLYPH, g);
    gl.position.copy(mid).addScaledVector(out, 0.035).add(V(0, -0.02, 0));
    gl.rotation.y = a;
    glowSprite(g, '#9fe07a', 0.3, gl.position.clone().addScaledVector(out, 0.03), 0.7 * dim);
  }
};

/**
 * Laboratory. 2 (a second elixir): a cauldron of violet brew bubbling on its own hearth at the front corner.
 * 3 (stronger elixirs): a copper retort on the roof's front gable, a fiery brew glowing in its cage.
 */
const lab = (g: THREE.Object3D, lv: number, mat: Mat, wear: Wear): void => {
  const dim = dimOf(wear);
  const cx = 0.14, cz = 0.4;
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const s = M(new THREE.DodecahedronGeometry(0.035, 0), i % 2 ? PLINTH : wallOf(mat), g);
    s.position.set(cx + Math.sin(a) * 0.1, 0.1, cz + Math.cos(a) * 0.1);
  }
  box(g, FORGE, 0.08, 0.025, 0.08, cx, 0.1, cz);
  const pot = M(new THREE.CylinderGeometry(0.09, 0.065, 0.1, 10), OIL_IRON, g);
  pot.position.set(cx, 0.17, cz);
  const lip = M(new THREE.TorusGeometry(0.09, 0.012, 4, 12), DSTEEL, g);
  lip.rotation.x = Math.PI / 2;
  lip.position.set(cx, 0.22, cz);
  const tilt = wear === 2 ? 0.08 : 0;
  const surf = M(new THREE.CircleGeometry(0.08, 10), brew('#b06bff'), g);
  surf.rotation.set(-Math.PI / 2 + tilt, 0, 0);
  surf.position.set(cx, 0.215, cz);
  for (const [x, z, s] of [[0.03, 0.01, 0.022], [-0.025, -0.02, 0.016]] as const) M(new THREE.SphereGeometry(s, 6, 4), brew('#b06bff'), g).position.set(cx + x, 0.22, cz + z);
  limb(g, V(cx + 0.03, 0.2, cz - 0.02), V(cx + 0.09, 0.36, cz - 0.08), 0.008, 0.008, WOOD, 4);
  glowSprite(g, '#b06bff', 0.4, V(cx, 0.27, cz), 0.65 * dim);
  glowSprite(g, '#ff7a2a', 0.22, V(cx, 0.1, cz + 0.08), 0.5 * dim);
  if (lv < 3) return;
  // On the front gable: a copper cage holding an orb of fire brew, a swan neck curling off it.
  const X = -0.14, front = 0.17;
  if (wear === 2) {
    const r = G(g, 0.0, 0.08, 0.42);
    r.rotation.z = 1.2;
    M(new THREE.SphereGeometry(0.07, 8, 6), COPPER, r);
    return;
  }
  const y = 1.0;
  const rg = G(g, X, y, front - 0.04);
  box(rg, COPPER, 0.12, 0.03, 0.12, 0, 0, 0);
  M(new THREE.SphereGeometry(0.065, 10, 8), brew('#ff8a2a'), rg).position.y = 0.1;
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    limb(rg, V(Math.sin(a) * 0.06, 0.01, Math.cos(a) * 0.06), V(Math.sin(a) * 0.05, 0.2, Math.cos(a) * 0.05), 0.007, 0.007, COPPER, 4);
  }
  M(new THREE.ConeGeometry(0.075, 0.08, 4), COPPER, rg).position.y = 0.24;
  const neck = new THREE.CatmullRomCurve3([V(0, 0.28, 0), V(0, 0.36, 0), V(0.08, 0.38, 0.02), V(0.14, 0.3, 0.04)]);
  M(new THREE.TubeGeometry(neck, 8, 0.012, 5), COPPER, rg);
  glowSprite(rg, '#ff8a2a', 0.55, V(0, 0.1, 0.03), 0.8 * dim);
};

/**
 * Workshop. 2 (one more clockwork turret): a turret bay against the right side, a finished turret head on its roof.
 * 3 (the overload rests less): a copper cooling tank on the bay, piped into a second chimney.
 */
const workshop = (g: THREE.Object3D, lv: number, mat: Mat, wear: Wear): void => {
  const wallM = wallOf(mat);
  const bx = 0.47, bz = -0.12, bw = 0.22, bd = 0.36, bh = 0.32;
  box(g, wallM, bw, bh, bd, bx, 0.08 + bh / 2, bz);
  if (mat === 1) for (const z of [-1, 1]) box(g, DWOOD, 0.035, bh, 0.035, bx + bw / 2, 0.08 + bh / 2, bz + (z * bd) / 2);
  else for (const z of [-1, 1]) box(g, stone(mat === 3 ? '#868a90' : '#a39d92'), 0.05, bh, 0.05, bx + bw / 2 - 0.01, 0.08 + bh / 2, bz + (z * bd) / 2 - z * 0.01);
  if (mat === 3) for (const y of [0.14, 0.36]) box(g, IRON_BAND, bw + 0.01, 0.025, bd + 0.01, bx, y, bz);
  // A flat roof with a dark door to the field.
  box(g, roofOf(mat), bw + 0.06, 0.035, bd + 0.06, bx, 0.08 + bh + 0.018, bz);
  box(g, BLACK, 0.012, 0.2, 0.14, bx + bw / 2 + 0.003, 0.18, bz + 0.06);
  for (const y of [0.13, 0.24]) box(g, IRON_BAND, 0.008, 0.02, 0.15, bx + bw / 2 + 0.008, y, bz + 0.06);
  const top = 0.08 + bh + 0.035;
  // The turret head on the bay: a drum, a gear on its flank, a short barrel to the field.
  const th = G(g, bx, top, bz + 0.06);
  if (wear === 2) th.rotation.z = 0.35;
  M(new THREE.CylinderGeometry(0.075, 0.085, 0.05, 10), DSTEEL, th).position.y = 0.025;
  const head = M(new THREE.CylinderGeometry(0.07, 0.07, 0.11, 10), mat === 1 ? COPPER : PLATE, th);
  head.rotation.z = Math.PI / 2;
  head.position.y = 0.11;
  const brl = M(new THREE.CylinderGeometry(0.022, 0.026, 0.16, 6), DSTEEL, th);
  brl.rotation.x = Math.PI / 2;
  brl.position.set(0, 0.12, 0.1);
  const sg = G(th, 0.062, 0.11, 0);
  sg.rotation.y = Math.PI / 2;
  gear(sg, 0.05, 8, COPPER, 0.018, IRON_BAND);
  if (lv < 3) return;
  // The cooling tank on the back of the bay and a second chimney piped to it.
  const tk = M(new THREE.CylinderGeometry(0.07, 0.07, 0.2, 10), COPPER, g);
  tk.rotation.x = Math.PI / 2;
  tk.position.set(bx, top + 0.08, bz - 0.11);
  for (const z of [-0.06, 0.06]) {
    const r = M(new THREE.TorusGeometry(0.072, 0.008, 3, 12), IRON_BAND, g);
    r.position.set(bx, top + 0.08, bz - 0.11 + z);
  }
  const chx = 0.55, chz = -0.33;
  const chTop = wear === 2 ? 0.75 : 1.18;
  box(g, wallM, 0.13, chTop - 0.08, 0.13, chx, (chTop + 0.08) / 2, chz);
  if (mat === 3) for (const y of [0.5, 0.9]) if (y < chTop) box(g, IRON_BAND, 0.14, 0.025, 0.14, chx, y, chz);
  if (wear < 2) {
    box(g, wallM, 0.16, 0.035, 0.16, chx, chTop, chz);
    box(g, BLACK, 0.09, 0.02, 0.09, chx, chTop + 0.012, chz);
    const cowl = M(new THREE.ConeGeometry(0.08, 0.06, 4), COPPER, g);
    cowl.position.set(chx, chTop + 0.1, chz);
    cowl.rotation.y = Math.PI / 4;
    for (const k of [-1, 1]) limb(g, V(chx + k * 0.05, chTop + 0.015, chz), V(chx + k * 0.04, chTop + 0.08, chz), 0.007, 0.007, COPPER, 4);
  }
  limb(g, V(bx + 0.02, top + 0.08, bz - 0.2), V(chx - 0.02, top + 0.12, chz + 0.06), 0.014, 0.014, COPPER, 5);
};

/**
 * Clan hall. 2 (up to 7 trophies): a trophy pole at the front left, two warlords' skulls and a shield on it.
 * 3 (longer drums): a great war drum on its frame at the front right, a red banner over it.
 */
const hall = (g: THREE.Object3D, lv: number, mat: Mat, wear: Wear): void => {
  const px = -0.36, pz = 0.4;
  const pole = G(g, px, 0.08, pz);
  if (wear === 2) pole.rotation.z = 0.25;
  box(pole, PLINTH, 0.12, 0.05, 0.12, 0, 0.025, 0);
  limb(pole, V(0, 0, 0), V(0, 0.82, 0), 0.026, 0.02, DWOOD, 6);
  box(pole, DWOOD, 0.26, 0.03, 0.03, 0, 0.66, 0);
  hornedSkull(pole, 0, 0.86, 0.01, 1.1, true);
  for (const k of [-1, 1]) hornedSkull(pole, k * 0.11, 0.58, 0.02, 0.8);
  roundShield(pole, 0, 0.4, 0.03, 0, 0, 0.085);
  for (const y of [0.2, 0.7]) box(pole, RED_BAND, 0.06, 0.03, 0.06, 0, y, 0);
  if (lv < 3) return;
  // The great war drum standing at the front right on three legs, its pale head up, red-banded and laced in iron,
  // its two bone beaters across it (crumbling: knocked over on its side).
  const dx = 0.36, dz = 0.4;
  const fallen = wear === 2;
  const dg = G(g, dx, 0.08, dz);
  if (fallen) {
    dg.rotation.set(0, 0.6, Math.PI / 2 - 0.1);
    dg.position.set(dx + 0.06, 0.08 + 0.17, dz);
  }
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.4;
    limb(dg, V(Math.sin(a) * 0.19, 0, Math.cos(a) * 0.19), V(Math.sin(a) * 0.14, 0.14, Math.cos(a) * 0.14), 0.016, 0.014, DWOOD, 4);
  }
  const dy = 0.24;
  M(new THREE.CylinderGeometry(0.17, 0.15, 0.22, 12), WOOD, dg).position.y = dy;
  M(new THREE.CylinderGeometry(0.178, 0.178, 0.014, 12), HIDE, dg).position.y = dy + 0.115;
  for (const y of [dy + 0.07, dy - 0.07]) M(new THREE.CylinderGeometry(0.168 - (y < dy ? 0.012 : 0), 0.168 - (y < dy ? 0.012 : 0), 0.035, 12, 1, true), RED_BAND, dg).position.y = y;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    limb(dg, V(Math.sin(a) * 0.168, dy + 0.1, Math.cos(a) * 0.168), V(Math.sin(a + 0.4) * 0.155, dy - 0.1, Math.cos(a + 0.4) * 0.155), 0.005, 0.005, DSTEEL, 3);
  }
  if (!fallen) for (const k of [-1, 1]) limb(dg, V(-0.12, dy + 0.13, k * 0.05), V(0.14, dy + 0.15, k * 0.09), 0.009, 0.014, BONE, 4);
};

/**
 * Watchtower. 2 (more reach for the towers near): long crimson hangings of the Order down the front of the shaft.
 * 3 (reveals farther): the Vigil's braziers burning at the four corners of the deck.
 */
const watchtower = (g: THREE.Object3D, lv: number, mat: Mat, wear: Wear): void => {
  const DECK = 1.08;
  const PAR = 0.36;
  const long = wear === 2 ? 0.3 : 0.5;
  // Hung over the parapet, on the two faces the table sees.
  for (const [x, z, ry] of [[PAR, 0, Math.PI / 2], [0, PAR, 0]] as const) {
    const hg = G(g, x, DECK + 0.16, z);
    hg.rotation.y = ry;
    box(hg, DWOOD, 0.3, 0.03, 0.03, 0, 0, 0.02);
    box(hg, CRIMSON, 0.2, long, 0.012, 0, -long / 2, 0.034);
    box(hg, EMBLEM, 0.07, 0.07, 0.014, 0, -long * 0.55, 0.04).rotation.z = Math.PI / 4;
    box(hg, DWOOD, 0.22, 0.02, 0.02, 0, -long, 0.034);
  }
  if (lv < 3) return;
  const dim = dimOf(wear);
  for (const [x, z] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
    if (wear === 2 && x > 0 && z < 0) continue;
    const bx = x * 0.3, bz = z * 0.3;
    limb(g, V(bx, DECK, bz), V(bx * 1.05, DECK + 0.2, bz * 1.05), 0.012, 0.012, DSTEEL, 4);
    const bowl = M(new THREE.CylinderGeometry(0.05, 0.03, 0.04, 6), SILVER, g);
    bowl.position.set(bx * 1.05, DECK + 0.22, bz * 1.05);
    M(new THREE.ConeGeometry(0.035, 0.1, 5), VIGIL_LIT, g).position.set(bx * 1.05, DECK + 0.29, bz * 1.05);
    glowSprite(g, '#dfe8ff', 0.36, V(bx * 1.05, DECK + 0.3, bz * 1.05), 0.75 * dim);
  }
};

/**
 * Sanctum. 2 (heals more): two braziers of the Vigil's flame flank the steps. 3 (blesses more): two tall
 * standards of the Order stand behind the shrine.
 */
const sanctum = (g: THREE.Object3D, lv: number, mat: Mat, wear: Wear): void => {
  const st = paleOf(mat);
  const dim = dimOf(wear);
  for (const k of [-1, 1]) {
    const x = k * 0.36, z = 0.48;
    const fallen = wear === 2 && k > 0;
    if (fallen) {
      const f = box(g, st, 0.08, 0.3, 0.08, x + 0.1, 0.05, z + 0.06);
      f.rotation.set(Math.PI / 2, 0, 0.6);
      continue;
    }
    box(g, st, 0.11, 0.04, 0.11, x, 0.02, z);
    box(g, st, 0.07, 0.3, 0.07, x, 0.19, z);
    const bowl = M(new THREE.CylinderGeometry(0.075, 0.04, 0.05, 8), SILVER, g);
    bowl.position.set(x, 0.365, z);
    M(new THREE.ConeGeometry(0.045, 0.14, 5), VIGIL_LIT, g).position.set(x, 0.46, z);
    glowSprite(g, '#dfe8ff', 0.42, V(x, 0.46, z), 0.75 * dim);
  }
  if (lv < 3) return;
  // Behind the shrine on both sides, their cloth turned to the table camera.
  for (const k of [-1, 1]) standard(g, k * 0.52, -0.36, 1.25, Math.PI / 4, wear, 0.04);
};

/**
 * Gathering post. 2 (reach 2): a waymark at the point of the yard, a lantern on its arm, sacks and crates piled by
 * it. 3 (reach 4): a tall signal mast at the back corner flying the long red banner, a second lantern on it.
 */
const depot = (g: THREE.Object3D, lv: number, mat: Mat, wear: Wear): void => {
  const Y0 = 0.07;
  lanternPost(g, 0.2, 0.66, 0.9, Math.PI / 4, wear, Y0);
  crate(g, 0.16, 0.38, Y0, 0.46, 0.3);
  crate(g, 0.13, 0.4, Y0 + 0.13, 0.46, -0.1);
  for (const [x, z, s] of [[0.5, 0.36, 0.065], [0.27, 0.56, 0.06]] as const) {
    const sk = M(new THREE.DodecahedronGeometry(s, 0), SACK, g);
    sk.position.set(x, Y0 + s * 0.7, z);
    sk.scale.set(1, 0.8, 0.85);
  }
  if (lv < 3) return;
  // The mast: on the left corner of the yard's back, braced, the banner toward the field.
  const mx = -0.74, mz = -0.32, h = 1.5;
  const mg = G(g, mx, Y0, mz);
  if (wear === 2) mg.rotation.z = 0.18;
  box(mg, PLINTH, 0.14, 0.05, 0.14, 0, 0.025, 0);
  box(mg, postOf(mat), 0.06, h, 0.06, 0, h / 2, 0);
  for (const k of [-1, 1]) limb(mg, V(k * 0.14, 0, 0.04), V(0, 0.45, 0), 0.013, 0.013, postOf(mat), 4);
  box(mg, DWOOD, 0.3, 0.03, 0.03, 0.1, h - 0.04, 0.02);
  const long = wear === 2 ? 0.3 : 0.5;
  box(mg, RED_BAND, 0.18, long, 0.012, 0.14, h - 0.06 - long / 2, 0.036);
  box(mg, EMBLEM, 0.06, 0.06, 0.014, 0.14, h - 0.2, 0.04).rotation.z = Math.PI / 4;
  M(new THREE.ConeGeometry(0.04, 0.1, 4), DSTEEL, mg).position.y = h + 0.05;
  const lan = G(mg, -0.07, h - 0.4, 0.03);
  M(new THREE.CylinderGeometry(0.03, 0.035, 0.06, 6, 1, true), DSTEEL, lan);
  box(lan, EMBER, 0.028, 0.04, 0.028, 0, 0, 0);
  M(new THREE.ConeGeometry(0.042, 0.035, 6), DSTEEL, lan).position.y = 0.045;
  glowSprite(lan, '#ff8a3a', 0.3, V(0, 0, 0.01), 0.75 * dimOf(wear));
};

/* ---------- traps (levels 2 to 5) ---------- */

/**
 * The stake pit. 2: a collar of sharpened stakes leaning out round the rim. 3: an iron band round the rim and
 * iron-shod tips on the collar. 4: three long iron spikes in the middle and more stakes. 5: black iron plates on
 * the rim, dragon-red runes burning in them.
 */
const spikes = (g: THREE.Object3D, lv: number): void => {
  const rng = mulberry32(7000 + lv);
  const n = lv >= 4 ? 14 : 10;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + 0.2;
    const r = 0.47;
    const og = G(g, Math.sin(a) * r, 0.02, Math.cos(a) * r);
    og.rotation.y = a;
    // Leaning out: tilted toward the group's +Z, which points away from the pit.
    const sg = G(og);
    sg.rotation.x = 0.5 + (rng() - 0.5) * 0.15;
    const len = 0.3 + rng() * 0.06;
    M(new THREE.CylinderGeometry(0.02, 0.024, len * 0.7, 5), WOOD, sg).position.y = len * 0.35;
    const tip = M(new THREE.ConeGeometry(0.021, len * 0.32, 5), lv >= 3 ? PLATE : WOOD, sg);
    tip.position.y = len * 0.7 + len * 0.16;
  }
  if (lv >= 3) {
    const band = M(new THREE.CylinderGeometry(0.5, 0.5, 0.05, 22, 1, true), IRON_BAND, g);
    band.position.y = 0.04;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      M(new THREE.OctahedronGeometry(0.018, 0), IRON_RIVET, g).position.set(Math.sin(a) * 0.505, 0.04, Math.cos(a) * 0.505);
    }
  }
  if (lv >= 4) {
    for (const [x, z, h, rx, rz] of [[0, 0, 0.55, 0, 0], [0.12, -0.08, 0.44, 0.15, -0.2], [-0.1, 0.09, 0.46, -0.15, 0.2]] as const) {
      const s = M(new THREE.ConeGeometry(0.04, h, 5), PLATE, g);
      s.position.set(x, h / 2, z);
      s.rotation.set(rx, 0, rz);
    }
  }
  if (lv >= 5) {
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
      const pg = G(g, Math.sin(a) * 0.515, 0.045, Math.cos(a) * 0.515);
      pg.rotation.y = a;
      box(pg, IRON_PLATE, 0.16, 0.08, 0.02, 0, 0, 0);
      box(pg, RUNE, 0.018, 0.055, 0.008, 0, 0, 0.012);
      for (const k of [-1, 1]) box(pg, RUNE, 0.03, 0.01, 0.008, k * 0.015, 0.02, 0.012).rotation.z = k * 0.7;
      if (i % 2 === 0) glowSprite(pg, '#ff6a4a', 0.32, V(0, 0, 0.05), 0.7);
    }
  }
};

/**
 * The oil cauldron. 2: a stone hearth and a fire of logs under the pot. 3: the pot banded in iron and a pouring
 * arm over it on a chain. 4: two iron-hooped casks of oil beside it. 5: iron spikes round the slick and the fire
 * stoked dragon red.
 */
const oil = (g: THREE.Object3D, lv: number): void => {
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const s = M(new THREE.DodecahedronGeometry(0.045, 0), i % 2 ? PLINTH : stone('#77736c'), g);
    s.position.set(Math.sin(a) * 0.17, 0.035, Math.cos(a) * 0.17);
  }
  for (const a of [0.4, 1.5, 2.6]) {
    const l = M(new THREE.CylinderGeometry(0.025, 0.028, 0.26, 6), WOOD, g);
    l.rotation.set(Math.PI / 2 - 0.25, a, 0);
    l.position.set(0, 0.07, 0);
  }
  box(g, lv >= 5 ? RUNE : FORGE, 0.12, 0.04, 0.12, 0, 0.06, 0);
  glowSprite(g, lv >= 5 ? '#ff4f3a' : '#ff7a2a', lv >= 5 ? 0.95 : 0.5, V(0, 0.16, 0), 0.8);
  if (lv >= 3) {
    // A heavy iron rim on the pot and a band under it.
    for (const [y, r, t] of [[0.575, 0.205, 0.022], [0.5, 0.19, 0.012]] as const) {
      const b = M(new THREE.TorusGeometry(r, t, 4, 16), IRON_BAND, g);
      b.rotation.x = Math.PI / 2;
      b.position.y = y;
    }
    // A pouring arm from the apex out over the slick, a chain down to the pot's lip.
    limb(g, V(0, 0.9, 0), V(0.32, 0.98, 0.22), 0.014, 0.012, DWOOD, 5);
    limb(g, V(0.32, 0.98, 0.22), V(0.16, 0.6, 0.1), 0.004, 0.004, DSTEEL, 3);
    M(new THREE.SphereGeometry(0.04, 6, 4), DSTEEL, g).position.set(-0.06, 0.92, -0.04);
  }
  if (lv >= 4) {
    for (const [x, z, h] of [[-0.4, 0.24, 0.22], [-0.5, 0.03, 0.19]] as const) {
      const c = M(new THREE.CylinderGeometry(0.085, 0.085, h, 9), WOOD, g);
      c.position.set(x, h / 2, z);
      for (const y of [0.03, h - 0.03]) {
        const r = M(new THREE.TorusGeometry(0.087, 0.008, 3, 12), DSTEEL, g);
        r.rotation.x = Math.PI / 2;
        r.position.set(x, y, z);
      }
      const top = M(new THREE.CircleGeometry(0.07, 9), BLACK, g);
      top.position.set(x, h + 0.002, z);
      top.rotation.x = -Math.PI / 2;
    }
  }
  if (lv >= 5) {
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2 + 0.15;
      const s = M(new THREE.ConeGeometry(0.03, 0.24, 5), DSTEEL, g);
      s.position.set(Math.sin(a) * 0.5, 0.1, Math.cos(a) * 0.5);
      s.rotation.set(Math.cos(a) * 0.45, 0, -Math.sin(a) * 0.45);
    }
  }
};

/**
 * Adds what `level` (2 and up) brings to a building or trap model of `type`. Level 1 and other types are left alone.
 */
export const levelUp = (m: Model, type: string, level: number, material: number, wear: Wear): Model => {
  if (level < 2) return m;
  const g = (m.root.getObjectByName('model') as THREE.Group | undefined) ?? m.root;
  const mat = (material === 3 ? 3 : material === 1 ? 1 : 2) as Mat;
  switch (type) {
    case 'temple':
      temple(g, level, mat, wear);
      if (level >= 3 && wear < 2) m.height = Math.max(m.height, 1.72);
      break;
    case 'grove':
      grove(g, level, mat, wear);
      break;
    case 'lab':
      lab(g, level, mat, wear);
      break;
    case 'workshop':
      workshop(g, level, mat, wear);
      break;
    case 'hall':
      hall(g, level, mat, wear);
      break;
    case 'watchtower':
      watchtower(g, level, mat, wear);
      break;
    case 'sanctum':
      sanctum(g, level, mat, wear);
      break;
    case 'depot':
      depot(g, level, mat, wear);
      break;
    case 'spikes':
      spikes(g, Math.min(5, level));
      break;
    case 'oil':
      oil(g, Math.min(5, level));
      break;
  }
  return m;
};
